import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { daysOfTheWeek, hours } from "./constants";
import { RRule, Weekday } from "rrule";
import {
  AvailabilitySlotType,
  NewAvailabilityDto,
  HOUR_LIMITS,
} from "../../utils/constants";

export const generateDynamicHours = (durationMinutes: number = 30) => {
  const hours = [];
  const startTime = HOUR_LIMITS.MIN_HOUR * 60;
  const maxTime = HOUR_LIMITS.MAX_HOUR * 60;

  const intervalMinutes = durationMinutes;

  let currentTime = startTime;
  let id = 1;

  while (currentTime <= maxTime) {
    const endTime = currentTime + durationMinutes;
    if (endTime <= maxTime) {
      const hour = Math.floor(currentTime / 60);
      const minute = currentTime % 60;
      const timeString = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;

      hours.push({
        id: `dynamic_${id}`,
        title: timeString,
        minutes: currentTime,
      });

      id++;
    }

    currentTime += intervalMinutes;
  }

  return hours;
};

export const getValidEndHours = (
  startHourId: string,
  durationMinutes: number,
  dynamicHours: any[]
) => {
  const startHour = dynamicHours.find((h) => h.id === startHourId);
  if (!startHour) return [];

  const startMinutes = startHour.minutes;
  const maxTime = HOUR_LIMITS.MAX_HOUR * 60;

  const intervalMinutes = durationMinutes;

  const endHours = [];
  let currentEndTime = startMinutes + durationMinutes;

  while (currentEndTime <= maxTime) {
    const hour = Math.floor(currentEndTime / 60);
    const minute = currentEndTime % 60;

    const timeString = `${hour.toString().padStart(2, "0")}:${minute
      .toString()
      .padStart(2, "0")}`;

    endHours.push({
      id: `end_${startHourId}_${currentEndTime}`,
      title: timeString,
      minutes: currentEndTime,
    });

    currentEndTime += intervalMinutes;
  }

  return endHours;
};

// Función para obtener el horario actual más cercano usando límites configurables
export const getCurrentDynamicId = (dynamicHours: any[]): string => {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  // Si estamos antes de la hora mínima, usar la hora mínima
  if (currentMinutes < HOUR_LIMITS.MIN_HOUR * 60) {
    return dynamicHours.length > 0 ? dynamicHours[0].id : "1";
  }

  // Si estamos después de la hora máxima, usar la última hora disponible
  if (currentMinutes > HOUR_LIMITS.MAX_HOUR * 60) {
    return dynamicHours.length > 0
      ? dynamicHours[dynamicHours.length - 1].id
      : "1";
  }

  let currentId = dynamicHours.length > 0 ? dynamicHours[0].id : "1";

  dynamicHours.forEach((hour) => {
    if (currentMinutes >= hour.minutes) {
      currentId = hour.id;
    }
  });

  return currentId;
};

// Función para obtener el siguiente horario válido usando horarios dinámicos
export const getNextDynamicId = (
  currentId: string,
  dynamicHours: any[]
): string => {
  const currentIndex = dynamicHours.findIndex((h) => h.id === currentId);

  if (currentIndex === -1 || currentIndex === dynamicHours.length - 1) {
    return currentId;
  }

  return dynamicHours[currentIndex + 1].id;
};

export const nextId = (currentId: string): string => {
  return (parseInt(currentId) + 1).toString();
};

export const generateRRule = (
  startDate: Date,
  byweekday: Weekday[],
  untilDate?: Date
) => {
  const rule = new RRule({
    freq: RRule.WEEKLY,
    interval: 1,
    byweekday,
    dtstart: startDate,
    until: untilDate,
  });

  return rule.toString();
};

export const hourStringToDate = (hour: string, date: Date) => {
  const hourString = hours.find((h) => h.id === hour);
  const [hourNumber, minuteNumber] = hourString!.title.split(":");
  const newDate = new Date(date);
  newDate.setHours(parseInt(hourNumber), parseInt(minuteNumber), 0, 0); // Limpiar segundos y milisegundos

  return newDate;
};

export const parseAvailabilityType = (
  remote: boolean,
  onSite: boolean
): AvailabilitySlotType => {
  let availabilitySlotType = AvailabilitySlotType.presentialVideocall;

  if (remote && !onSite) {
    availabilitySlotType = AvailabilitySlotType.videocall;
  }

  if (!remote && onSite) {
    availabilitySlotType = AvailabilitySlotType.presential;
  }

  return availabilitySlotType;
};

// Función para convertir horarios dinámicos a fecha
export const dynamicHourStringToDate = (
  hourId: string,
  date: Date,
  dynamicHours: any[]
) => {
  const hourData = dynamicHours.find((h) => h.id === hourId);
  if (!hourData) {
    // Si no se encuentra en horarios dinámicos, buscar en validEndHours
    const endHourMatch = hourId.match(/^end_.*_(\d+)$/);
    if (endHourMatch) {
      const minutes = parseInt(endHourMatch[1]);
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const newDate = new Date(date);
      newDate.setHours(hour, minute, 0, 0); // Limpiar segundos y milisegundos
      return newDate;
    }
    return date;
  }

  const hour = Math.floor(hourData.minutes / 60);
  const minute = hourData.minutes % 60;
  const newDate = new Date(date);
  newDate.setHours(hour, minute, 0, 0); // Limpiar segundos y milisegundos
  return newDate;
};

// Función parseData actualizada para usar horarios dinámicos
export const parseDataWithDynamicHours = (
  data: any,
  dynamicHours: any[]
): NewAvailabilityDto => {
  const {
    date,
    startHour,
    endHour,
    remote,
    onSite,
    validity,
    selectedDays,
    duration,
  } = data;

  let untilDate = date;
  switch (validity) {
    case "1":
      untilDate = addDays(date, 6);
      break;
    case "2":
      untilDate = addWeeks(date, 1);
      break;
    case "3":
      untilDate = addWeeks(date, 2);
      break;
    case "4":
      untilDate = addMonths(date, 1);
      break;
    case "5":
      untilDate = addMonths(date, 2);
      break;
    default:
      break;
  }

  const startDate = dynamicHourStringToDate(startHour, date, dynamicHours);
  const endDate = dynamicHourStringToDate(endHour, date, dynamicHours);
  untilDate = dynamicHourStringToDate(startHour, untilDate, dynamicHours);

  const availabilitySlotType = parseAvailabilityType(remote, onSite);

  const byweekday: Weekday[] = selectedDays.map((day: string) => {
    const selectedDayData = daysOfTheWeek.find((dayData) => day === dayData.id);
    return selectedDayData?.rrule;
  });
  const rrule: string = generateRRule(startDate, byweekday, untilDate);

  return {
    startDate,
    endDate,
    availabilitySlotType,
    rrule,
    duration: duration ? parseInt(duration) : undefined,
  };
};

// Función parseData original para compatibilidad
export const parseData = (data: any): NewAvailabilityDto => {
  const {
    date,
    startHour,
    endHour,
    remote,
    onSite,
    validity,
    selectedDays,
    duration,
  } = data;

  let untilDate = date;
  switch (validity) {
    case "1":
      untilDate = addDays(date, 6);
      break;
    case "2":
      untilDate = addWeeks(date, 1);
      break;
    case "3":
      untilDate = addWeeks(date, 2);
      break;
    case "4":
      untilDate = addMonths(date, 1);
      break;
    case "5":
      untilDate = addMonths(date, 2);
      break;
    default:
      break;
  }

  const startDate = hourStringToDate(startHour, date);
  const endDate = hourStringToDate(endHour, date);
  untilDate = hourStringToDate(startHour, untilDate);

  const availabilitySlotType = parseAvailabilityType(remote, onSite);

  const byweekday: Weekday[] = selectedDays.map((day: string) => {
    const selectedDayData = daysOfTheWeek.find((dayData) => day === dayData.id);
    return selectedDayData?.rrule;
  });
  const rrule: string = generateRRule(startDate, byweekday, untilDate);

  return {
    startDate,
    endDate,
    availabilitySlotType,
    rrule,
    duration: duration ? parseInt(duration) : undefined,
  };
};

export const getWeekDateRange = (date: Date) => {
  const startDate = startOfWeek(date, { weekStartsOn: 0 });
  startDate.setHours(0, 0, 0, 0);
  const endDate = addDays(endOfWeek(date, { weekStartsOn: 0 }), 1);

  endDate.setHours(0, 0, 0, 0);

  return { startDate, endDate };
};

export const getMonthDateRange = (date: Date) => {
  const startDate = startOfMonth(date);
  startDate.setHours(0, 0, 0, 0);

  const lastDayOfMonth = endOfMonth(date);
  const endDate = addDays(lastDayOfMonth, 1);
  endDate.setHours(0, 0, 0, 0);

  return { startDate, endDate };
};
